import React, { useEffect, useRef } from "react";
import { FaShieldAlt, FaLock, FaCookie, FaUserSecret, FaFileContract, FaGavel, FaEnvelope, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import { MdPrivacyTip, MdUpdate, MdSecurity } from "react-icons/md";

const PrivacyPolicy = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // Trigger animations when section comes into view
          entries[0].target.classList.add('in-view');
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const policySections = [
    {
      icon: <FaUserSecret />,
      title: "Information We Collect",
      content: "We collect information you provide directly, including name, email address, phone number, and company details when you contact us or use our services. We also automatically collect usage data, IP addresses, and device information to improve our services."
    },
    {
      icon: <FaFileContract />,
      title: "How We Use Your Information",
      content: "Your information helps us provide, maintain, and improve our services, communicate with you about updates, respond to your inquiries, and personalize your experience. We may also use it for analytics and security purposes."
    },
    {
      icon: <FaLock />,
      title: "Data Security",
      content: "We implement industry-standard security measures including encryption, access controls, and regular security audits to protect your personal information from unauthorized access, disclosure, or destruction."
    },
    {
      icon: <FaCookie />,
      title: "Cookies & Tracking",
      content: "We use cookies and similar technologies to enhance your browsing experience, analyze site traffic, and personalize content. You can control cookie preferences through your browser settings."
    },
    {
      icon: <MdSecurity />,
      title: "Third-Party Disclosure",
      content: "We do not sell, trade, or transfer your personal information to external parties without your consent, except for trusted third parties who assist in operating our website or conducting our business, subject to confidentiality agreements."
    },
    {
      icon: <MdUpdate />,
      title: "Your Rights & Choices",
      content: "You have the right to access, correct, or delete your personal information. You may opt-out of marketing communications at any time and can request a copy of your data stored with us."
    }
  ];

  return (
    <section id="privacy-policy" className="privacy-policy" ref={sectionRef}>
      {/* Background Elements */}
      <div className="background-elements">
        <div className="floating-circle circle-1"></div>
        <div className="floating-circle circle-2"></div>
        <div className="floating-circle circle-3"></div>
        <div className="grid-pattern"></div>
      </div>

      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <div className="section-badge">
            <span className="badge-dot"></span>
            <span className="badge-text">LEGAL & COMPLIANCE</span>
          </div>
          
          <h1 className="section-title">
            <span className="title-line title-line-1">Privacy</span>
            <span className="title-line title-line-2">
              <span className="gradient-text">& Security</span>
            </span>
            <span className="title-line title-line-3">Policy</span>
          </h1>
          
          <p className="section-subtitle">
            Last Updated: January 2024 | Version 2.0
          </p>
        </div>

        {/* Introduction Card */}
        <div className="intro-card">
          <div className="intro-icon">
            <FaShieldAlt />
          </div>
          <div className="intro-content">
            <h2>Your Privacy Matters to Us</h2>
            <p>
              At Codriva Tech, we are committed to protecting your privacy and ensuring 
              the security of your personal information. This policy outlines how we collect, 
              use, and safeguard your data when you interact with our services, website, 
              and applications. By using our services, you consent to the practices described 
              in this policy.
            </p>
          </div>
        </div>

        {/* Policy Grid */}
        <div className="policy-grid">
          {policySections.map((section, index) => (
            <div 
              key={index} 
              className="policy-card"
              style={{ animationDelay: `${0.2 + index * 0.1}s` }}
            >
              <div className="policy-icon-wrapper">
                {section.icon}
              </div>
              <h3>{section.title}</h3>
              <p>{section.content}</p>
            </div>
          ))}
        </div>

        {/* Important Notes */}
        <div className="important-notes">
          <h3 className="notes-title">
            <span className="gradient-text">Important Information</span>
          </h3>
          
          <div className="notes-grid">
            <div className="note-item">
              <div className="note-badge">01</div>
              <div className="note-content">
                <h4>Data Retention</h4>
                <p>We retain your personal information only as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law.</p>
              </div>
            </div>
            
            <div className="note-item">
              <div className="note-badge">02</div>
              <div className="note-content">
                <h4>International Transfers</h4>
                <p>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers.</p>
              </div>
            </div>
            
            <div className="note-item">
              <div className="note-badge">03</div>
              <div className="note-content">
                <h4>Children's Privacy</h4>
                <p>Our services are not directed to individuals under 13. We do not knowingly collect personal information from children without parental consent.</p>
              </div>
            </div>
            
            <div className="note-item">
              <div className="note-badge">04</div>
              <div className="note-content">
                <h4>Policy Updates</h4>
                <p>We may update this policy periodically. We will notify you of material changes by posting the new policy on this page with an updated revision date.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="contact-section">
          <div className="contact-card">
            <h3>
              <FaGavel className="contact-icon" />
              Have Questions About Your Privacy?
            </h3>
            <p>
              Our data protection team is here to address any concerns or inquiries 
              you may have about your privacy rights or this policy.
            </p>
            
            <div className="contact-details">
              <div className="contact-item">
                <FaEnvelope />
                <span>privacy@codrivatech.com</span>
              </div>
              <div className="contact-item">
                <FaPhone />
                <span>+91 (751) 234-5678</span>
              </div>
              <div className="contact-item">
                <FaMapMarkerAlt />
                <span>Gwalior, Madhya Pradesh, India</span>
              </div>
            </div>
            
            <button className="contact-button">
              Contact Privacy Officer
            </button>
          </div>
        </div>

        {/* Footer Note */}
        <div className="policy-footer">
          <div className="footer-note">
            <MdPrivacyTip />
            <p>
              By continuing to use our services, you acknowledge that you have read 
              and understood this Privacy Policy. For more detailed information about 
              our data practices, please contact our privacy team.
            </p>
          </div>
          
          <div className="footer-links">
            <a href="#terms">Terms of Service</a>
            <span className="separator">•</span>
            <a href="#cookies">Cookie Policy</a>
            <span className="separator">•</span>
            <a href="#gdpr">GDPR Compliance</a>
          </div>
        </div>
      </div>

      <style jsx>{`
        .privacy-policy {
          padding: 120px 0;
          background: linear-gradient(135deg, 
            #020617 0%,
            #0f172a 30%,
            #1e293b 70%,
            #0f172a 100%);
          color: #f8fafc;
          position: relative;
          overflow: hidden;
          min-height: 100vh;
        }

        /* Background Elements */
        .background-elements {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .grid-pattern {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            linear-gradient(rgba(34, 211, 238, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34, 211, 238, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
        }

        .floating-circle {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(34, 211, 238, 0.1);
          animation: float 8s ease-in-out infinite;
        }

        .circle-1 {
          width: 300px;
          height: 300px;
          top: 5%;
          right: -100px;
          animation-delay: 0s;
        }

        .circle-2 {
          width: 400px;
          height: 400px;
          bottom: -100px;
          left: -100px;
          animation-delay: -2s;
          animation-duration: 10s;
        }

        .circle-3 {
          width: 200px;
          height: 200px;
          top: 50%;
          left: 80%;
          animation-delay: -4s;
          animation-duration: 12s;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 32px;
          position: relative;
          z-index: 2;
        }

        /* Section Header */
        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(34, 211, 238, 0.1);
          padding: 10px 24px;
          border-radius: 50px;
          margin-bottom: 32px;
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s ease forwards;
        }

        .badge-dot {
          width: 8px;
          height: 8px;
          background: #22d3ee;
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        .badge-text {
          font-size: 0.9rem;
          font-weight: 600;
          color: #94a3b8;
          letter-spacing: 1px;
        }

        .section-title {
          font-size: 4.5rem;
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 16px;
        }

        .title-line {
          display: block;
          opacity: 0;
          transform: translateY(30px);
          animation: slideUp 0.8s ease forwards;
        }

        .title-line-1 {
          animation-delay: 0.2s;
        }

        .title-line-2 {
          animation-delay: 0.4s;
        }

        .title-line-3 {
          animation-delay: 0.6s;
        }

        .gradient-text {
          background: linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .section-subtitle {
          color: #94a3b8;
          font-size: 1rem;
          letter-spacing: 2px;
          opacity: 0;
          animation: fadeIn 0.8s ease 0.8s forwards;
        }

        /* Introduction Card */
        .intro-card {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 30px;
          padding: 50px;
          margin-bottom: 80px;
          display: flex;
          gap: 40px;
          align-items: flex-start;
          backdrop-filter: blur(10px);
          opacity: 0;
          transform: translateY(30px);
          animation: fadeInUp 0.8s ease 0.8s forwards;
        }

        .intro-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #22d3ee20, #3b82f620);
          border-radius: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #22d3ee;
          font-size: 2.5rem;
          flex-shrink: 0;
          border: 1px solid rgba(34, 211, 238, 0.2);
        }

        .intro-content h2 {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 20px;
          color: #f8fafc;
        }

        .intro-content p {
          font-size: 1.1rem;
          line-height: 1.8;
          color: #cbd5e1;
          max-width: 900px;
        }

        /* Policy Grid */
        .policy-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
          margin-bottom: 100px;
        }

        .policy-card {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 24px;
          padding: 40px 30px;
          backdrop-filter: blur(10px);
          transition: all 0.4s ease;
          opacity: 0;
          transform: translateY(30px);
          animation: fadeInUp 0.8s ease forwards;
        }

        .policy-card:hover {
          transform: translateY(-10px);
          border-color: rgba(34, 211, 238, 0.2);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          background: rgba(255, 255, 255, 0.03);
        }

        .policy-icon-wrapper {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #22d3ee15, #3b82f615);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 30px;
          color: #22d3ee;
          font-size: 2rem;
          transition: all 0.4s ease;
        }

        .policy-card:hover .policy-icon-wrapper {
          transform: scale(1.1) rotate(5deg);
          background: linear-gradient(135deg, #22d3ee30, #3b82f630);
        }

        .policy-card h3 {
          font-size: 1.3rem;
          font-weight: 700;
          margin-bottom: 15px;
          color: #f8fafc;
        }

        .policy-card p {
          font-size: 0.95rem;
          line-height: 1.7;
          color: #94a3b8;
        }

        /* Important Notes */
        .important-notes {
          margin-bottom: 100px;
        }

        .notes-title {
          font-size: 2.5rem;
          font-weight: 700;
          text-align: center;
          margin-bottom: 60px;
        }

        .notes-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 30px;
        }

        .note-item {
          display: flex;
          gap: 20px;
          padding: 30px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 20px;
          backdrop-filter: blur(10px);
          transition: all 0.4s ease;
          opacity: 0;
          transform: translateY(30px);
          animation: fadeInUp 0.8s ease forwards;
        }

        .note-item:hover {
          border-color: rgba(34, 211, 238, 0.2);
          transform: translateX(10px);
        }

        .note-badge {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #22d3ee20, #3b82f620);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #22d3ee;
          font-weight: 700;
          font-size: 1.2rem;
          flex-shrink: 0;
          border: 1px solid rgba(34, 211, 238, 0.2);
        }

        .note-content h4 {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 10px;
          color: #f8fafc;
        }

        .note-content p {
          font-size: 0.95rem;
          line-height: 1.6;
          color: #94a3b8;
        }

        /* Contact Section */
        .contact-section {
          margin-bottom: 60px;
        }

        .contact-card {
          background: linear-gradient(135deg, #22d3ee10, #3b82f610);
          border: 1px solid rgba(34, 211, 238, 0.1);
          border-radius: 40px;
          padding: 60px;
          text-align: center;
          backdrop-filter: blur(10px);
        }

        .contact-card h3 {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 15px;
        }

        .contact-icon {
          color: #22d3ee;
        }

        .contact-card p {
          font-size: 1.1rem;
          color: #cbd5e1;
          max-width: 600px;
          margin: 0 auto 40px;
        }

        .contact-details {
          display: flex;
          justify-content: center;
          gap: 50px;
          margin-bottom: 40px;
          flex-wrap: wrap;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #94a3b8;
          font-size: 1rem;
        }

        .contact-item svg {
          color: #22d3ee;
        }

        .contact-button {
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          border: none;
          padding: 16px 40px;
          border-radius: 50px;
          color: white;
          font-weight: 600;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.4s ease;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .contact-button:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(34, 211, 238, 0.3);
        }

        /* Footer */
        .policy-footer {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .footer-note {
          display: flex;
          align-items: center;
          gap: 20px;
          color: #94a3b8;
          font-size: 0.95rem;
          line-height: 1.6;
          margin-bottom: 30px;
        }

        .footer-note svg {
          color: #22d3ee;
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .footer-links {
          display: flex;
          justify-content: center;
          gap: 20px;
          color: #94a3b8;
        }

        .footer-links a {
          color: #94a3b8;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .footer-links a:hover {
          color: #22d3ee;
        }

        .separator {
          color: #334155;
        }

        /* Animations */
        @keyframes fadeIn {
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.2);
          }
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .section-title {
            font-size: 3.5rem;
          }
          
          .policy-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 1024px) {
          .intro-card {
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 40px;
          }

          .notes-grid {
            grid-template-columns: 1fr;
          }

          .contact-details {
            flex-direction: column;
            gap: 20px;
          }
        }

        @media (max-width: 768px) {
          .privacy-policy {
            padding: 100px 0;
          }

          .section-title {
            font-size: 2.5rem;
          }

          .policy-grid {
            grid-template-columns: 1fr;
          }

          .intro-card {
            padding: 30px;
          }

          .intro-content h2 {
            font-size: 1.8rem;
          }

          .intro-content p {
            font-size: 1rem;
          }

          .contact-card {
            padding: 40px 20px;
          }

          .contact-card h3 {
            font-size: 1.5rem;
            flex-direction: column;
          }

          .note-item {
            flex-direction: column;
            text-align: center;
          }

          .note-badge {
            margin: 0 auto;
          }

          .footer-note {
            flex-direction: column;
            text-align: center;
          }

          .footer-links {
            flex-direction: column;
            align-items: center;
            gap: 10px;
          }

          .separator {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 20px;
          }

          .section-title {
            font-size: 2rem;
          }

          .intro-card {
            padding: 20px;
          }
        }
      `}</style>
    </section>
  );
};

export default PrivacyPolicy;